import React from "react";
import {
  Badge,
  Flex,
  Icon,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from "@chakra-ui/react";
import { IoCheckmarkDoneCircleSharp } from "react-icons/io5";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";

const AnalyticsTable = ({
  title,
  amount,
  captions,
  data = [],
  isLoading,
  variant,
}) => {
  const textColor = useColorModeValue(
    "gray.700",
    "white"
  );

  const formatCurrency = (value) =>
    `P${Number(value || 0).toLocaleString()}`;

  const getBadge = (status) => {
    switch (
      String(status || "")
        .toLowerCase()
        .trim()
    ) {
      case "paid":
      case "resolved":
      case "completed":
        return "green";

      case "pending":
      case "investigating":
        return "orange";

      case "flagged":
      case "failed":
        return "red";

      default:
        return "gray";
    }
  };

  const renderRow = (
    row,
    index
  ) => {
    switch (variant) {
      case "partnerPerformance":
        return (
          <>
            <Td fontWeight="bold">
              {index === 0
                ? "🥇"
                : index === 1
                ? "🥈"
                : index === 2
                ? "🥉"
                : `#${index + 1}`}
            </Td>

            <Td>
              {row.partner_name ||
                "Unknown"}
            </Td>

            <Td>
              {row.referral_code}
            </Td>

            <Td>
              {
                row.drivers_referred
              }
            </Td>

            <Td>
              {
                row.milestones_completed
              }
            </Td>

            <Td>
              {formatCurrency(
                row.commission_earned
              )}
            </Td>
          </>
        );

      case "topPartners":
        return (
          <>
            <Td fontWeight="bold">
              {index === 0
                ? "🥇"
                : index === 1
                ? "🥈"
                : index === 2
                ? "🥉"
                : `#${index + 1}`}
            </Td>

            <Td>
              {row.partner_name ||
                "Unknown"}
            </Td>

            <Td>
              {row.referral_code}
            </Td>

            <Td>
              {
                row.drivers_referred
              }
            </Td>

            <Td>
              {formatCurrency(
                row.commission_earned
              )}
            </Td>
          </>
        );

      case "paidCommissions":
        return (
          <>
            <Td>
              {row.partner_name}
            </Td>

            <Td>
              {row.driver_name}
            </Td>

            <Td>
              {formatCurrency(
                row.amount
              )}
            </Td>

            <Td>
              {row.payment_date}
            </Td>

            <Td>
              <Badge
                colorScheme={getBadge(
                  row.status
                )}
              >
                {row.status}
              </Badge>
            </Td>
          </>
        );

      case "suspicious":
        return (
          <>
            <Td>
              {row.partner_name}
            </Td>

            <Td>
              {row.driver_name}
            </Td>

            <Td>
              {row.reason}
            </Td>

            <Td>
              {row.created_at}
            </Td>

            <Td>
              <Badge
                colorScheme={getBadge(
                  row.status
                )}
              >
                {row.status}
              </Badge>
            </Td>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Card
      p="16px"
      overflowX={{
        sm: "scroll",
        xl: "hidden",
      }}
    >
      <CardHeader p="12px 0px 28px 0px">
        <Flex direction="column">
          <Text
            fontSize="lg"
            color={textColor}
            fontWeight="bold"
            pb=".5rem"
          >
            {isLoading ? (
              <Skeleton width={180} />
            ) : (
              title
            )}
          </Text>

          <Flex align="center">
            {isLoading ? (
              <Skeleton
                circle
                width={16}
                height={16}
              />
            ) : (
              <Icon
                as={
                  IoCheckmarkDoneCircleSharp
                }
                color="teal.300"
                w={4}
                h={4}
                pe="3px"
              />
            )}

            <Text
              ml={2}
              color="gray.400"
            >
              {isLoading ? (
                <Skeleton width={120} />
              ) : (
                <>
                  <Text
                    as="span"
                    fontWeight="bold"
                  >
                    {amount}
                  </Text>{" "}
                  records
                </>
              )}
            </Text>
          </Flex>
        </Flex>
      </CardHeader>

      <Table
        variant="simple"
        color={textColor}
      >
        <Thead>
          <Tr>
            {captions.map(
              (
                caption,
                index
              ) => (
                <Th
                  key={index}
                  color="gray.400"
                >
                  {isLoading ? (
                    <Skeleton width={70} />
                  ) : (
                    caption
                  )}
                </Th>
              )
            )}
          </Tr>
        </Thead>

        <Tbody>
          {isLoading ? (
            Array(5)
              .fill(0)
              .map((_, row) => (
                <Tr key={row}>
                  {captions.map(
                    (
                      _,
                      col
                    ) => (
                      <Td
                        key={col}
                      >
                        <Skeleton />
                      </Td>
                    )
                  )}
                </Tr>
              ))
          ) : data.length > 0 ? (
            data.map(
              (
                row,
                index
              ) => (
                <Tr key={index}>
                  {renderRow(
                    row,
                    index
                  )}
                </Tr>
              )
            )
          ) : (
            <Tr>
              <Td
                colSpan={
                  captions.length
                }
                textAlign="center"
              >
                <Text color="gray.500">
                  No data available.
                </Text>
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
    </Card>
  );
};

export default AnalyticsTable;